import { useCallback, useState, useEffect } from 'react';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  useAnimatedScrollHandler,
} from 'react-native-reanimated';
import { Keyboard, LayoutChangeEvent } from 'react-native';
import { UseStickyButtonProps } from './useStickyButton.types';

export const useStickyButton = ({
  buttonHeight = 56,
  bottomPadding = 40,
  topPadding = 20,
  extraOffset = 0,
  fieldsEndY,
}: UseStickyButtonProps) => {
  const [contentHeight, setContentHeight] = useState(0);
  const [contentY, setContentY] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(0);

  const scrollY = useSharedValue(0);
  const keyboardHeight = useSharedValue(0);

  useEffect(() => {
    const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
      keyboardHeight.value = withTiming(e.endCoordinates.height, {
        duration: e.duration,
      });
    });

    const keyboardWillHide = Keyboard.addListener('keyboardWillHide', (e) => {
      keyboardHeight.value = withTiming(0, {
        duration: e.duration,
      });
    });

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, [keyboardHeight]);

  const buttonAnimatedStyle = useAnimatedStyle(() => {
    const scrollPosition = scrollY.value;
    const contentTop = contentY;
    const contentBottom = contentY + contentHeight;
    const availableScreenHeight = scrollViewHeight - keyboardHeight.value;
    const screenBottom = scrollPosition + availableScreenHeight;

    const contentVisibleTop = Math.max(contentTop, scrollPosition);
    const contentVisibleBottom = Math.min(contentBottom, screenBottom);
    const isContentVisible = contentVisibleTop < contentVisibleBottom;

    if (!isContentVisible) {
      return {
        opacity: 0,
      };
    }

    let targetPosition = screenBottom - buttonHeight - bottomPadding;

    if (fieldsEndY !== undefined && fieldsEndY > 0) {
      const fieldsEndPosition = fieldsEndY + topPadding;

      if (
        fieldsEndPosition >= scrollPosition &&
        fieldsEndPosition + buttonHeight <= screenBottom
      ) {
        targetPosition = fieldsEndPosition;
      } else if (fieldsEndPosition < scrollPosition) {
        targetPosition = scrollPosition + topPadding;
      } else {
        targetPosition = screenBottom - buttonHeight - bottomPadding;
      }
    }

    const maxPosition = contentBottom - buttonHeight - topPadding;
    const finalPosition = Math.min(targetPosition, maxPosition);

    return {
      opacity: 1,
      position: 'absolute',
      top: finalPosition - scrollPosition,
      left: 0,
      right: 0,
    };
  }, [
    contentHeight,
    contentY,
    scrollViewHeight,
    keyboardHeight,
    buttonHeight,
    bottomPadding,
    topPadding,
    fieldsEndY,
  ]);

  const handleContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height, y } = event.nativeEvent.layout;
      setContentHeight(height + extraOffset);
      setContentY(y);
    },
    [extraOffset],
  );

  const handleScrollViewLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setScrollViewHeight(height);
  }, []);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  return {
    buttonAnimatedStyle,
    handleContentLayout,
    handleScrollViewLayout,
    scrollHandler,
  };
};
